import {useLocaleStore} from '@/stores/localeStore';
import debounce from 'lodash/debounce';
import {provide, inject, type MaybeRef, unref} from 'vue';
import {useScriptTag} from '@vueuse/core';
import {environment} from '@/config/environment';

type MatomoInstance = {
    push: (action: MatomoAction) => void;
};

type MatomoAction = [string, ...MatomoValue[]];
type MatomoValue = string | number | undefined | null;

type CustomWindow = {
    _paq?: unknown;
} & Window;

declare const window: CustomWindow;

const debounceTime = 200;

initMatomo();

/**
 * Tracks a view for the current page debounced.
 *
 * The debounced function is using a relatively small debounce time to prevent
 * view tracking of automatic page changes (automatic redirects, component navigation guards etc.)
 */
export const trackMatomoPageView = debounce((routePath?: string) => {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }
    _paq.push(['setCustomUrl', routePath ?? window.location.href]);
    _paq.push(['setDocumentTitle', document.title]);
    // customDimension with id 1 -> "locale"
    _paq.push(['setCustomDimension', 1, useLocaleStore().currentLocale]);
    _paq.push(['trackPageView']);
}, debounceTime);

export function trackMatomoEvent(
    category: string,
    action: string,
    name?: string,
    value?: string | number
) {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }

    const trackArray: MatomoAction = ['trackEvent', category, action];
    if (name || value) {
        trackArray.push(name);
    }
    if (value) {
        trackArray.push(value);
    }
    _paq.push(trackArray);
}

/**
 * Returns an eventTracker object that allows tracking of events of a certain category.
 * If a category is passed, this category will be used and also provided to child components using `provide`.
 * If no category is passed, it will inject an event tracker that was provided by a parent, if found -
 * otherwise a no-op stub is used.
 */
export function useMatomoEventTracker(category?: MaybeRef<string>) {
    const key = 'eventTracker';

    if (category) {
        const eventTracker = {
            trackEvent(action: string, name?: string, value?: string | number) {
                trackMatomoEvent(unref(category), action, name, value);
            },
        };
        provide(key, eventTracker);

        return eventTracker;
    } else {
        return inject(key, {trackEvent() {}});
    }
}

export function setMatomoUserId(userId?: string | null) {
    const _paq = getMatomoInstance();
    if (!_paq) {
        return;
    }

    _paq.push(['setUserId', userId]);
}

function getMatomoInstance(): MatomoInstance | null {
    const _paq = window._paq;
    if (_paq && typeof _paq === 'object' && 'push' in _paq && typeof _paq.push === 'function') {
        return _paq as MatomoInstance;
    }

    return null;
}

function initMatomo() {
    const url = environment.VITE_MATOMO_URL;
    const siteId = environment.VITE_MATOMO_SITE_ID;
    if (!url || !siteId) {
        return;
    }

    window._paq = window._paq || [];
    const _paq = window._paq as MatomoInstance;

    _paq.push(['setCustomDimension', 2, __IS_HYBRID_APP__ ? 'app' : 'web']);

    _paq.push(['enableLinkTracking']);
    _paq.push(['setTrackerUrl', url + 'matomo.php']);
    _paq.push(['setSiteId', siteId]);

    // matomo.js is frequently blocked - by ad/tracking blockers, Safari content
    // blockers and the tracking protection of embedded webviews (e.g. the MS Teams
    // tab iframe). useScriptTag rejects its load promise with the script's error
    // Event in that case, so catch it to avoid an unhandled promise rejection.
    const {load} = useScriptTag(url + 'matomo.js', undefined, {manual: true});
    load().catch(() => {});
}

export const booking2TrackingIndicator = 'Booking 2.0';
